import { useEffect, useRef, useState } from "react";
import Draggable from "react-draggable";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../Interceptor";

const AiAssistent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const sampleQuestions = [
    "Create a project for me?",
    "Delete a transaction?",
    "How can I update a team member access?",
    "How can I delete my account?",
    "Add transaction Office Rent 500 on 2026-02-10 in project cortiq.",
  ];
  const [showHint, setShowHint] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const [pendingOptions, setPendingOptions] = useState(null);
  const [autoSubmittedOptionKey, setAutoSubmittedOptionKey] = useState(null);
  const [isListening, setIsListening] = useState(false);
  const autoCloseTimer = useRef(null);
  const isDragging = useRef(false);
  const endRef = useRef(null);
  const recognitionRef = useRef(null);
  const voiceCommittedRef = useRef("");
  const inputRef = useRef("");

  const mergeVoiceText = (existingText, incomingText) => {
    const existing = String(existingText || "").trim();
    const incoming = String(incomingText || "").trim();
    if (!incoming) return existing;
    if (!existing) return incoming;
    if (existing.toLowerCase().endsWith(incoming.toLowerCase()))
      return existing;

    const existingWords = existing.split(/\s+/);
    const incomingWords = incoming.split(/\s+/);
    const maxOverlap = Math.min(existingWords.length, incomingWords.length, 8);
    let overlap = 0;
    for (let i = maxOverlap; i > 0; i -= 1) {
      const existingTail = existingWords.slice(-i).join(" ").toLowerCase();
      const incomingHead = incomingWords.slice(0, i).join(" ").toLowerCase();
      if (existingTail === incomingHead) {
        overlap = i;
        break;
      }
    }
    const rest = incomingWords.slice(overlap).join(" ").trim();
    return rest ? `${existing} ${rest}` : existing;
  };

  useEffect(() => {
    setShowHint(true);
    autoCloseTimer.current = setTimeout(() => setShowHint(false), 5000);
    return () => clearTimeout(autoCloseTimer.current);
  }, []);

  useEffect(() => {
    if (endRef.current) {
      endRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isOpen]);

  useEffect(() => {
    inputRef.current = input;
  }, [input]);

  useEffect(() => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) return;

    const recognition = new SpeechRecognition();
    recognition.lang = navigator.language || "en-US";
    recognition.interimResults = true;
    recognition.continuous = true; // Keep listening until manually stopped

    recognition.onresult = (event) => {
      let finalTranscript = "";
      let interimTranscript = "";

      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const piece = event.results[i][0]?.transcript?.trim();
        if (!piece) continue;
        if (event.results[i].isFinal) {
          finalTranscript = `${finalTranscript} ${piece}`.trim();
        } else {
          interimTranscript = `${interimTranscript} ${piece}`.trim();
        }
      }

      if (finalTranscript) {
        voiceCommittedRef.current = mergeVoiceText(
          voiceCommittedRef.current,
          finalTranscript,
        );
      }

      const nextInput = [voiceCommittedRef.current, interimTranscript]
        .filter(Boolean)
        .join(" ")
        .trim();

      if (nextInput) {
        setInput(nextInput);
      }
    };

    recognition.onstart = () => {
      voiceCommittedRef.current = inputRef.current.trim();
      setIsListening(true);
    };

    recognition.onerror = () => {
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognitionRef.current = recognition;

    return () => {
      recognition.stop();
    };
  }, []);

  const handleToggle = () => {
    if (isDragging.current) return;
    clearTimeout(autoCloseTimer.current);
    setShowHint(false);
    setIsOpen((prev) => !prev);
  };

  const handleDrag = () => {
    if (!isDragging.current) {
      isDragging.current = true;
      clearTimeout(autoCloseTimer.current);
      setShowHint(false);
    }
  };

  const handleDragStop = () => {
    setTimeout(() => {
      isDragging.current = false;
    }, 0);
  };

  const stopVoiceInput = () => {
    if (!recognitionRef.current) return;
    try {
      recognitionRef.current.stop();
    } catch {
      // ignore
    }
    setIsListening(false);
  };

  const appendMessage = (role, text) => {
    setMessages((prev) => [...prev, { role, text }]);
  };

  const getOptionPayload = (items, selectedIndex, fallbackValue) => {
    const option = Array.isArray(items) ? items[selectedIndex - 1] : null;

    if (!option) {
      const byValue = Array.isArray(items)
        ? items.find(
            (item) =>
              String(item.value ?? item.id ?? item.label) ===
              String(fallbackValue),
          )
        : null;
      const submitValue = String(
        byValue?.value ?? byValue?.id ?? fallbackValue ?? "",
      ).trim();
      const displayText = String(byValue?.label ?? submitValue).trim();
      return { submitValue, displayText };
    }

    const submitValue = String(
      option?.value ?? option?.id ?? fallbackValue ?? "",
    ).trim();
    const displayText = String(option?.label ?? submitValue).trim();
    return { submitValue, displayText };
  };

  const handleSend = async (overrideText) => {
    const isObjectPayload =
      overrideText &&
      typeof overrideText === "object" &&
      !Array.isArray(overrideText);
    const submitValue = isObjectPayload
      ? String(overrideText.submitValue ?? "").trim()
      : String(overrideText ?? input).trim();
    const displayValue = isObjectPayload
      ? String(overrideText.displayText ?? submitValue).trim()
      : submitValue;

    if (!submitValue || isSending) return;
    stopVoiceInput();
    setInput("");
    voiceCommittedRef.current = "";
    appendMessage("user", displayValue);
    setIsSending(true);
    setPendingOptions(null);
    try {
      const response = await api.post("admin/ai/chat", {
        message: submitValue,
      });
      if (response.data?.status === "success" && response.data?.action) {
        const action = response.data.action;
        const isUserRoute = location.pathname.startsWith("/user");
        const mapUserRoute = (route) => {
          if (!isUserRoute) return route;
          if (route === "/dashboard") return "/user/dashboard";
          if (route === "/addExpense") return "/user/addExpense";
          if (route === "/expenseList") return "/user/expenseList";
          if (route === "/chats") return "/user/chat";
          if (route === "/project") return "/user/project";
          return route;
        };
        window.dispatchEvent(
          new CustomEvent("ai:refresh", { detail: { action } }),
        );
        if (action === "open_route" && response.data?.route) {
          navigate(mapUserRoute(response.data.route));
        }
        if (action === "open_project_detail") {
          const route = mapUserRoute(response.data?.route || "/project");
          navigate(route, {
            state: {
              projectId: response.data?.projectId,
              projectName: response.data?.projectName,
            },
          });
        }
        if (action === "open_create_invoice") {
          navigate("/createInvoice");
        }
        if (action.includes("project") || action.includes("milestone")) {
          window.dispatchEvent(new Event("ai:refreshProjects"));
        }
        if (action.includes("expense")) {
          window.dispatchEvent(new Event("ai:refreshExpenses"));
        }
        if (action.includes("invoice")) {
          window.dispatchEvent(new Event("ai:refreshInvoices"));
        }
        if (action.includes("member") || action.includes("user")) {
          window.dispatchEvent(new Event("ai:refreshMembers"));
        }
      }
      setPendingOptions(response.data?.options || null);
      appendMessage("assistant", response.data?.reply || "Done.");
    } catch (error) {
      setPendingOptions(null);
      appendMessage(
        "assistant",
        error?.response?.data?.reply || "Something went wrong.",
      );
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleToggleVoice = () => {
    if (!recognitionRef.current) return;

    if (isListening) {
      stopVoiceInput(); // Manual stop
    } else {
      try {
        recognitionRef.current.start(); // Manual start
      } catch {
        setIsListening(false);
      }
    }
  };

  const handleClear = async () => {
    stopVoiceInput();
    setInput("");
    voiceCommittedRef.current = "";
    setMessages([]);
    setPendingOptions(null);
    setAutoSubmittedOptionKey(null);
    try {
      await api.post("admin/ai/chat", { message: "/clear" });
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    const isSingleSelectableOption =
      pendingOptions?.type !== "currency" &&
      Array.isArray(pendingOptions?.items) &&
      pendingOptions.items.length === 1;
    if (!isSingleSelectableOption || isSending) return;

    const only = pendingOptions.items[0];
    const submitValue = String(
      only?.value ?? only?.id ?? only?.label ?? "",
    ).trim();
    const displayText = String(only?.label ?? submitValue).trim();
    if (!submitValue) return;

    const nextKey = `${pendingOptions?.type}:${submitValue}`;
    if (autoSubmittedOptionKey === nextKey) return;

    setAutoSubmittedOptionKey(nextKey);
    handleSend({ submitValue, displayText });
  }, [pendingOptions, isSending, autoSubmittedOptionKey]);

  useEffect(() => {
    if (!pendingOptions) {
      setAutoSubmittedOptionKey(null);
    }
  }, [pendingOptions]);

  return (
    <Draggable
      bounds="body"
      handle=".ai-drag-handle"
      onDrag={handleDrag}
      onStop={handleDragStop}
    >
      <div className="fixed z-[99999] bottom-2 right-2 flex flex-col items-end gap-2">
        {showHint && !isOpen && (
          <div className="relative overflow-hidden bg-gradient-to-br from-blue-800 via-blue-500 to-blue-800 shadow-2xl rounded-xl border border-blue-300 p-2 pr-4 max-w-[90vw] sm:max-w-xs lg:max-w-md">
            <div className="absolute top-0 right-0 w-32 h-32 bg-blue-200 rounded-full -mr-16 -mt-16 opacity-20"></div>
            <p className="text-sm sm:text-base font-semibold text-white">
              👋 Hi, I am your AI assistant
            </p>
          </div>
        )}

        {isOpen && (
          <div className="w-[90vw] sm:w-96 bg-white border border-blue-100 shadow-2xl rounded-2xl overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 bg-themeColor text-white">
              <div className="font-semibold">AI Assistant</div>
              <div className="flex items-center gap-2">
                <button
                  className="text-white text-sm opacity-90 hover:opacity-100"
                  onClick={handleClear}
                >
                  <i class="fa-solid fa-arrows-rotate"></i>
                </button>
                <button
                  className="text-white text-sm opacity-90 hover:opacity-100"
                  onClick={() => {
                    stopVoiceInput();
                    setIsOpen(false);
                  }}
                >
                  Close
                </button>
              </div>
            </div>
            <div className="h-72 sm:h-80 overflow-y-auto px-3 py-3 space-y-2 bg-slate-50">
              {messages.length === 0 && (
                <div className="space-y-2">
                  <div className="flex flex-wrap gap-2 justify-center">
                    {sampleQuestions.map((question) => (
                      <button
                        key={question}
                        type="button"
                        disabled={isSending}
                        onClick={() => handleSend(question)}
                        className="text-center text-xs sm:text-sm px-3 py-2 rounded-xl border border-slate-200 bg-white text-slate-700 hover:bg-slate-100 disabled:opacity-60"
                      >
                        {question}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((msg, idx) => (
                <div
                  key={`${msg.role}-${idx}`}
                  className={`text-sm px-3 py-2 rounded-xl max-w-[85%] ${
                    msg.role === "user"
                      ? "ml-auto bg-blue-600 text-white"
                      : "mr-auto bg-white border border-slate-200 text-slate-700"
                  } whitespace-pre-wrap`}
                >
                  {msg.text}
                </div>
              ))}
              <div ref={endRef} />
            </div>
            <div className="px-3 py-3 border-t border-slate-200 bg-white">
              {pendingOptions?.type === "currency" && (
                <div className="mb-3">
                  <label className="text-xs text-slate-500">
                    Select currency
                  </label>
                  <select
                    key={`currency-${pendingOptions?.items?.length || 0}`}
                    className="mt-1 w-full border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-themeColor"
                    onChange={(e) => {
                      const selectedIdx = e.target.selectedIndex - 1;
                      const payload = getOptionPayload(
                        pendingOptions?.items,
                        selectedIdx + 1,
                        e.target.value,
                      );
                      setPendingOptions(null);
                      handleSend(payload);
                    }}
                    defaultValue=""
                  >
                    <option value="" disabled>
                      Choose currency
                    </option>
                    {pendingOptions.items?.map((opt, idx) => (
                      <option
                        key={`${opt.value ?? opt.id ?? opt.label}-${idx}`}
                        value={opt.value ?? opt.id ?? opt.label ?? ""}
                      >
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {pendingOptions?.items?.length > 0 &&
                pendingOptions?.type !== "currency" && (
                  <div className="mb-3">
                    <label className="text-xs text-slate-500">
                      {pendingOptions?.type === "project"
                        ? "Select project"
                        : pendingOptions?.type === "milestone"
                          ? "Select milestone"
                          : "Select option"}
                    </label>
                    <select
                      key={`${pendingOptions?.type}-${pendingOptions?.items
                        ?.map(
                          (opt) => opt?.value ?? opt?.id ?? opt?.label ?? "",
                        )
                        .join("|")}`}
                      className="mt-1 w-full border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-themeColor"
                      onChange={(e) => {
                        const selectedIdx = e.target.selectedIndex - 1;
                        const payload = getOptionPayload(
                          pendingOptions?.items,
                          selectedIdx + 1,
                          e.target.value,
                        );
                        setPendingOptions(null);
                        handleSend(payload);
                      }}
                      defaultValue=""
                    >
                      <option value="" disabled>
                        Choose option
                      </option>
                      {pendingOptions.items?.map((opt, idx) => (
                        <option
                          key={`${opt.value ?? opt.id ?? opt.label}-${idx}`}
                          value={opt.value ?? opt.id ?? opt.label ?? ""}
                        >
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              <textarea
                className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-themeColor resize-none"
                rows={2}
                placeholder="Type a message"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
              />
              <div className="flex gap-1 w-full">
                <button
                  onClick={handleToggleVoice}
                  disabled={isSending}
                  className={`relative mt-2 h-10 w-12 rounded-xl py-2 text-sm font-semibold disabled:opacity-60 overflow-hidden transition-colors duration-200 ${
                    isListening ? "bg-red-600" : "bg-themeGradient"
                  }`}
                >
                  {isListening && (
                    <>
                      <span className="absolute inset-0 rounded-xl bg-red-300/30 animate-pulse"></span>
                      <span className="absolute -inset-1 rounded-xl border border-red-200/60 animate-ping"></span>
                    </>
                  )}
                  <i
                    className={`fa-solid text-white ${
                      isListening
                        ? "fa-microphone-lines animate-bounce"
                        : "fa-microphone"
                    }`}
                  ></i>
                </button>
                <button
                  onClick={() => handleSend()}
                  disabled={isSending}
                  className="mt-2 w-full bg-themeGradient text-white rounded-xl py-2 text-sm font-semibold disabled:opacity-60"
                >
                  {isSending ? (
                    "Sending..."
                  ) : (
                    <>
                      Send <i className="fa-solid fa-paper-plane text-xs"></i>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        <div
          onClick={handleToggle}
          onTouchEnd={handleToggle}
          className="ai-drag-handle group relative rounded-full cursor-pointer h-12 w-12 sm:h-14 sm:w-14 lg:h-20 lg:w-20 hover:scale-110 active:scale-95 transition-all duration-300 ease-out shadow-[0_10px_40px_rgba(59,130,246,0.5)] hover:shadow-[0_15px_50px_rgba(59,130,246,0.7)]"
        >
          <div className="absolute inset-0 rounded-full bg-gradient-to-br from-blue-400 via-purple-500 to-pink-500 p-[3px] animate-pulse">
            <div className="h-full w-full rounded-full bg-white"></div>
          </div>
          <div className="absolute inset-[3px] rounded-full bg-gradient-to-br from-blue-500 via-purple-600 to-pink-600 animate-gradient-shift"></div>
          <div className="absolute inset-0 rounded-full bg-blue-400 opacity-0 group-hover:opacity-30 blur-xl transition-opacity duration-300"></div>
          <div className="relative h-full w-full rounded-full overflow-hidden p-[3px]">
            <img
              src="/ai.gif"
              className="h-full w-full rounded-full object-cover relative z-10"
              alt="AI Assistant"
            />
          </div>
        </div>

        <style jsx>{`
          @keyframes gradient-shift {
            0%,
            100% {
              background-position: 0% 50%;
            }
            50% {
              background-position: 100% 50%;
            }
          }
          .animate-gradient-shift {
            background-size: 200% 200%;
            animation: gradient-shift 3s ease infinite;
          }
        `}</style>
      </div>
    </Draggable>
  );
};

export default AiAssistent;
